import mapboxgl, { AttributionControl } from "mapbox-gl";
import MapboxDraw, { DrawCustomMode } from "@mapbox/mapbox-gl-draw";
import equal from "fast-deep-equal";

import {
    ButtonGroupControl,
    ButtonGroupControlOptions,
    IconButtonControl,
    IconButtonControlOptions,
} from "./mapbox-control";

export const customDrawModes: { [modeKey: string]: DrawCustomMode } = {};

interface FeatureState {
    [key: string]: any;
}

type Feature =
    | mapboxgl.GeoJSONFeature
    | mapboxgl.FeatureSelector
    | mapboxgl.TargetFeature;

type FeatureStateEntry = [Feature, FeatureState];

interface CommandRegistry {
    [key: string]: Array<(map: mapboxgl.Map) => void>;
}

export const commandRegistry: CommandRegistry = {};

interface DrawCommandRegistry {
    [key: string]: Array<(map: MapboxDraw) => void>;
}

export const drawCommandRegistry: DrawCommandRegistry = {};

interface EventListenerWithFeatures {
    features?: mapboxgl.GeoJSONFeature[];
    perPointFeatures?: mapboxgl.GeoJSONFeature[][];
    points?: mapboxgl.Point[];
    point: mapboxgl.PointLike;
}

interface AttributionControlConfig {
    type: "attribution";
    options?: mapboxgl.AttributionControlOptions;
    position?: mapboxgl.ControlPosition;
}

interface FullscreenControlConfig {
    type: "fullscreen";
    options?: mapboxgl.FullscreenControlOptions;
    position?: mapboxgl.ControlPosition;
}

interface NavigationControlConfig {
    type: "navigation";
    options?: mapboxgl.NavigationControlOptions;
    position?: mapboxgl.ControlPosition;
}

interface ScaleControlConfig {
    type: "scale";
    options?: mapboxgl.ScaleControlOptions;
    position?: mapboxgl.ControlPosition;
}

interface IconButtonControlConfig {
    type: "icon-button";
    id: string;
    options?: IconButtonControlOptions;
    position?: mapboxgl.ControlPosition;
}

interface ButtonGroupControlConfig {
    type: "button-group";
    id: string;
    options?: ButtonGroupControlOptions;
    position?: mapboxgl.ControlPosition;
}

interface DrawControlConfig {
    type: "draw";
    options?: MapboxDraw.MapboxDrawOptions;
    modeKeys?: string[];
    position?: mapboxgl.ControlPosition;
}

type ControlConfig =
    | AttributionControlConfig
    | FullscreenControlConfig
    | NavigationControlConfig
    | ScaleControlConfig
    | IconButtonControlConfig
    | ButtonGroupControlConfig
    | DrawControlConfig;

export class MapboxMap extends HTMLElement {
    private _map?: mapboxgl.Map;
    private _draw?: MapboxDraw;
    private _style?: string | mapboxgl.StyleSpecification;
    private _controls: Map<string, mapboxgl.IControl> = new Map();
    private _controlConfigs: ControlConfig[] = [];
    private _minZoom?: number;
    private _maxZoom?: number;
    private _maxBounds?: mapboxgl.LngLatBoundsLike;
    private _renderWorldCopies: boolean = true;
    private _refreshExpiredTiles: boolean = true;
    private _dragRotate: boolean = true;
    private _touchZoom: boolean = true;
    private _touchRotate: boolean = true;
    private _featureState: FeatureStateEntry[] = [];
    private _eventRegistrationQueue: {
        [type: string]: Array<(event: any) => void>;
    } = {};
    private _eventListenerMap: Map<(event: any) => void, (event: any) => void> =
        new Map();
    private _resizeObserver = new ResizeObserver(() => {
        this._map?.resize();
    });

    public initialCenter?: mapboxgl.LngLatLike;
    public initialZoom?: number;
    public initialBearing?: number;
    public initialPitch?: number;
    public interactive: boolean = true;
    public eventFeaturesLayers?: string[];
    public eventFeaturesFilter?: any[];
    public logoPosition?:
        | "top-left"
        | "top-right"
        | "bottom-left"
        | "bottom-right";
    public token?: string;

    get mapboxStyle(): string | mapboxgl.StyleSpecification | undefined {
        return this._style;
    }

    set mapboxStyle(value: string | mapboxgl.StyleSpecification | undefined) {
        if (this._map && value && !equal(value, this._style))
            // setStyle generates a flicker with mapbox-gl-draw
            this._map.setStyle(value);
        this._style = value;
    }

    get minZoom(): number | undefined {
        return this._minZoom;
    }

    set minZoom(value: number | undefined) {
        if (this._map && value !== undefined) this._map.setMinZoom(value);
        this._minZoom = value;
    }

    get maxZoom(): number | undefined {
        return this._maxZoom;
    }

    set maxZoom(value: number | undefined) {
        if (this._map && value !== undefined) this._map.setMaxZoom(value);
        this._maxZoom = value;
    }

    get map(): mapboxgl.Map | undefined {
        return this._map;
    }

    get controlConfigs(): ControlConfig[] {
        return this._controlConfigs;
    }

    set controlConfigs(value: ControlConfig[]) {
        if (this._map) {
            for (const controlConfig of value) {
                switch (controlConfig.type) {
                    case "icon-button":
                        const button = this._controls.get(controlConfig.id);
                        if (button) {
                            if (
                                button instanceof IconButtonControl &&
                                controlConfig.options
                            ) {
                                button.buttonClassName =
                                    controlConfig.options.buttonClassName;
                            }
                        } else {
                            this.addIconButtonControl(
                                controlConfig.id,
                                controlConfig.options || {
                                    name: "",
                                    buttonClassName: "",
                                },
                                controlConfig.position,
                            );
                        }
                        break;
                    case "button-group":
                        const group = this._controls.get(controlConfig.id);
                        if (
                            group &&
                            group instanceof ButtonGroupControl &&
                            controlConfig.options
                        ) {
                            group.buttons = controlConfig.options.buttons;
                            group.selectedValue =
                                controlConfig.options.selectedValue;
                        }
                        break;
                    // case "draw":
                    //     Add/remove draw control if necessary. Options can’t be changed.
                    //     break;
                }
            }
        }
        this._controlConfigs = value;
    }

    get maxBounds(): mapboxgl.LngLatBoundsLike | undefined {
        return this._maxBounds;
    }

    set maxBounds(value: mapboxgl.LngLatBoundsLike | undefined) {
        if (this._map && value) this._map.setMaxBounds(value);
        this._maxBounds = value;
    }

    get renderWorldCopies(): boolean {
        return this._renderWorldCopies;
    }

    set renderWorldCopies(value: boolean) {
        if (this._map) this._map.setRenderWorldCopies(value);
        this._renderWorldCopies = value;
    }

    get dragRotate(): boolean {
        return this._dragRotate;
    }

    set dragRotate(value: boolean) {
        if (this._map) {
            if (value) {
                this._map.dragRotate.enable();
            } else {
                this._map.dragRotate.disable();
            }
        }
        this._dragRotate = value;
    }

    get touchZoom(): boolean {
        return this._touchZoom;
    }

    set touchZoom(value: boolean) {
        if (this._map) {
            if (value) {
                this._map.touchZoomRotate.enable();
            } else {
                this._map.touchZoomRotate.disable();
            }
        }
        this._touchZoom = value;
    }

    get touchRotate(): boolean {
        return this._touchRotate;
    }

    set touchRotate(value: boolean) {
        if (this._map) {
            if (value) {
                this._map.touchZoomRotate.enableRotation();
            } else {
                this._map.touchZoomRotate.disableRotation();
            }
        }
        this._touchRotate = value;
    }

    get featureState(): FeatureStateEntry[] {
        return this._featureState;
    }

    set featureState(value: FeatureStateEntry[]) {
        // TODO: Clean this up
        function makeId({ id, source, sourceLayer }: Feature): string {
            return `${id}::${source}::${sourceLayer || ""}`;
        }

        if (this._map) {
            const map = new Map<
                string,
                { feature: Feature; state: FeatureState }
            >(
                this._featureState.map(([feature, state]) => [
                    makeId(feature),
                    { feature, state },
                ]),
            );

            value.forEach(([feature, state]) => {
                const id = makeId(feature);
                if (map.has(id)) {
                    const prevValue = map.get(id)!.state;
                    const keys = Object.keys(prevValue);
                    const newValue: FeatureState = {};
                    keys.forEach((k) => {
                        if (state[k] === undefined) {
                            newValue[k] = undefined;
                        }
                    });
                    this._map!.setFeatureState(
                        feature,
                        Object.assign(newValue, state),
                    );
                } else {
                    this._map!.setFeatureState(feature, state);
                }
                map.delete(id);
            });

            map.forEach(({ feature, state }) => {
                const keys = Object.keys(state);
                const newValue: FeatureState = {};
                keys.forEach((k) => {
                    newValue[k] = undefined;
                });
                this._map!.setFeatureState(feature, newValue);
            });
        }

        this._featureState = value;
    }

    addEventListener(
        type: string,
        fn: (event: any) => void,
        ...args: any[]
    ): void {
        if (this._map) {
            let wrapped: (event: any) => void;

            if (type == "controlclick") {
                // add the listener directly to this element, not the map
                super.addEventListener(type, fn, ...args);
                return;
            } else if (
                [
                    "mousedown",
                    "mouseup",
                    "mouseover",
                    "mousemove",
                    "click",
                    "dblclick",
                    "mouseout",
                    "contextmenu",
                    "zoom",
                    "zoomstart",
                    "zoomend",
                    "rotate",
                    "rotatestart",
                    "rotateend",
                ].includes(type)
            ) {
                wrapped = (e: EventListenerWithFeatures) => {
                    e.features = this._map!.queryRenderedFeatures(e.point, {
                        layers: this.eventFeaturesLayers,
                        filter: this.eventFeaturesFilter,
                    });
                    return fn(e);
                };
            } else if (
                ["touchend", "touchmove", "touchcancel"].includes(type)
            ) {
                wrapped = (e: EventListenerWithFeatures) => {
                    e.features = this._map!.queryRenderedFeatures(e.point, {
                        layers: this.eventFeaturesLayers,
                        filter: this.eventFeaturesFilter,
                    });
                    if (e.points) {
                        e.perPointFeatures = e.points.map(
                            (point: mapboxgl.Point) =>
                                this._map!.queryRenderedFeatures(point, {
                                    layers: this.eventFeaturesLayers,
                                    filter: this.eventFeaturesFilter,
                                }),
                        );
                    }
                    return fn(e);
                };
            } else if (["resize", "idle", "move"].includes(type)) {
                // augment event with viewport state
                wrapped = (e) => {
                    e.center = this._map?.getCenter();
                    e.zoom = this._map?.getZoom();
                    e.bearing = this._map?.getBearing();
                    e.pitch = this._map?.getPitch();
                    const bounds = this._map?.getBounds();
                    if (bounds)
                        e.bounds = {
                            sw: bounds.getSouthWest(),
                            ne: bounds.getNorthEast(),
                        };
                    return fn(e);
                };
            } else if (type === "movestart") {
                wrapped = (e) => {
                    if (e.originalEvent) e.manualMove = true;
                    else e.manualMove = false;
                    return fn(e);
                };
            } else {
                wrapped = fn;
            }

            this._eventListenerMap.set(fn, wrapped);
            this._map.on(type as any, wrapped);
        } else {
            this._eventRegistrationQueue[type] =
                this._eventRegistrationQueue[type] || [];
            this._eventRegistrationQueue[type].push(fn);
        }
    }

    removeEventListener(
        type: string,
        fn: (event: any) => void,
        ...args: any[]
    ): void {
        if (this._map) {
            const wrapped = this._eventListenerMap.get(fn);
            if (wrapped) {
                this._eventListenerMap.delete(fn);
                this._map.off(type as any, wrapped);
            }
        } else {
            const queue = this._eventRegistrationQueue[type] || [];
            const index = queue.findIndex((listener) => listener === fn);
            if (index >= 0) {
                queue.splice(index, 1);
            }
        }
    }

    private addIconButtonControl(
        id: string,
        options: IconButtonControlOptions,
        position?: mapboxgl.ControlPosition,
    ) {
        const iconButtonControl = new IconButtonControl(
            options || {
                name: "button",
                buttonClassName: "",
            },
            () => {
                this.dispatchEvent(
                    new CustomEvent("controlclick", {
                        detail: {
                            id: id,
                            value: null,
                        },
                    }),
                );
            },
        );

        this._map?.addControl(iconButtonControl, position);
        this._controls.set(id, iconButtonControl);
    }

    private _createMapInstance(): mapboxgl.Map {
        const mapOptions: mapboxgl.MapOptions = {
            container: this,
            style: this._style || "mapbox://styles/mapbox/standard",
            minZoom: this._minZoom || 0,
            maxZoom: this._maxZoom || 22,
            interactive: this.interactive,
            attributionControl: false,
            logoPosition: this.logoPosition || "bottom-left",
            refreshExpiredTiles: this._refreshExpiredTiles,
            maxBounds: this._maxBounds,
            dragRotate: this._dragRotate,
            renderWorldCopies: this._renderWorldCopies,
        };

        if (this.initialCenter) {
            mapOptions.center = this.initialCenter;
        }
        if (this.initialZoom !== undefined) {
            mapOptions.zoom = this.initialZoom;
        }
        if (this.initialBearing !== undefined) {
            mapOptions.bearing = this.initialBearing;
        }
        if (this.initialPitch !== undefined) {
            mapOptions.pitch = this.initialPitch;
        }

        this._map = new mapboxgl.Map(mapOptions);

        if (mapboxgl.getRTLTextPluginStatus() === "unloaded") {
            mapboxgl.setRTLTextPlugin(
                "https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.3.0/mapbox-gl-rtl-text.js",
                null,
                true, // Lazy load the plugin
            );
        }

        if (this._touchZoom) {
            this._map.touchZoomRotate.enable();
        } else {
            this._map.touchZoomRotate.disable();
        }

        if (!this._touchRotate) {
            this._map.touchZoomRotate.disableRotation();
        }

        this._resizeObserver.observe(this._map.getContainer());

        for (const controlConfig of this.controlConfigs) {
            switch (controlConfig.type) {
                case "attribution":
                    this._map.addControl(
                        new AttributionControl(controlConfig.options),
                        controlConfig.position,
                    );
                    break;
                case "fullscreen":
                    this._map.addControl(
                        new mapboxgl.FullscreenControl(controlConfig.options),
                        controlConfig.position,
                    );
                    break;
                case "navigation":
                    this._map.addControl(
                        new mapboxgl.NavigationControl(controlConfig.options),
                        controlConfig.position,
                    );
                    break;
                case "scale":
                    this._map.addControl(
                        new mapboxgl.ScaleControl(controlConfig.options),
                        controlConfig.position,
                    );
                    break;
                case "icon-button":
                    this.addIconButtonControl(
                        controlConfig.id,
                        controlConfig.options || {
                            name: "",
                            buttonClassName: "",
                        },
                        controlConfig.position,
                    );
                    break;
                case "button-group":
                    const buttonGroupControl = new ButtonGroupControl(
                        controlConfig.options || { buttons: [] },
                        (value) => {
                            this.dispatchEvent(
                                new CustomEvent("controlclick", {
                                    detail: {
                                        id: controlConfig.id,
                                        value: value,
                                    },
                                }),
                            );
                        },
                    );

                    this._map.addControl(
                        buttonGroupControl,
                        controlConfig.position,
                    );
                    this._controls.set(controlConfig.id, buttonGroupControl);
                    break;

                case "draw":
                    // Wire up custom & default draw modes specified by modeKeys, OR leave unset for default
                    const allModes: any = {
                        ...MapboxDraw.modes,
                        ...customDrawModes,
                    };
                    const options: MapboxDraw.MapboxDrawOptions =
                        controlConfig.options || {};
                    if (controlConfig.modeKeys) {
                        options.modes = Object.keys(allModes)
                            .filter((modeKey) =>
                                controlConfig.modeKeys?.includes(modeKey),
                            )
                            .reduce((obj: any, key) => {
                                obj[key] = allModes[key];
                                return obj;
                            }, {});
                    }
                    const drawControl = new MapboxDraw(options);
                    this._map.addControl(drawControl, controlConfig.position);
                    this._draw = drawControl;
                    if (drawCommandRegistry[this.id]) {
                        // TODO: how do I check to see if the map has already loaded?
                        this._map.on("load", () => {
                            let cmd: ((draw: MapboxDraw) => void) | undefined;
                            while (
                                (cmd = drawCommandRegistry[this.id].shift())
                            ) {
                                cmd(drawControl);
                            }
                        });
                    }
                    break;
            }
        }

        Object.entries(this._eventRegistrationQueue).forEach(
            ([type, listeners]) => {
                listeners.forEach((listener) => {
                    this.addEventListener(type, listener);
                });
            },
        );

        this._eventRegistrationQueue = {};

        if (commandRegistry[this.id]) {
            this._map.on("load", () => {
                let cmd: ((map: mapboxgl.Map) => void) | undefined;
                while ((cmd = commandRegistry[this.id].shift())) {
                    cmd(this._map!);
                }
            });
        }

        return this._map;
    }

    connectedCallback(): void {
        if (this.token) {
            mapboxgl.accessToken = this.token;
        }
        this.style.display = "block";
        this.style.width = "100%";
        this.style.height = "100%";

        this._upgradeProperty("mapboxStyle");
        this._upgradeProperty("minZoom");
        this._upgradeProperty("maxZoom");
        this._upgradeProperty("maxBounds");
        this._upgradeProperty("renderWorldCopies");
        this._upgradeProperty("center");
        this._upgradeProperty("zoom");
        this._upgradeProperty("bearing");
        this._upgradeProperty("pitch");
        this._upgradeProperty("featureState");

        this._map = this._createMapInstance();
    }

    private _upgradeProperty(prop: string): void {
        if (this.hasOwnProperty(prop)) {
            const value = (this as any)[prop];
            delete (this as any)[prop];
            (this as any)[prop] = value;
        }
    }

    disconnectedCallback(): void {
        if (this._map) {
            this._map.remove();
            delete this._map;
        }
        this._resizeObserver.disconnect();
    }
}
